import React, { useEffect, useRef, useState } from 'react';

const ITEMS = [
    {
        key: 'management',
        title: 'MANAGEMENT',
        subtitle: 'Planned project, organized workflow',
        description: 'Efficiently manage your app with strategic planning and development to avoid wasting time',
    },
    {
        key: 'outsource',
        title: 'OUTSOURCE',
        subtitle: 'Fresh, powerful, more experienced',
        description: 'Enhance your team with our expert services while optimizing your budget',
    },
    {
        key: 'rework',
        title: 'REWORK',
        subtitle: 'Solutions, increased success rate',
        description: 'Complex problems that seem unsolvable can actually be untangled by starting over or resuming from a checkpoint',
    },
];

const STAGGER_MS = [420, 560, 720];
/* A switch left off turns itself back on after this. It is a demonstration of
 * the three approaches, not a settings panel — the switches are the animation.
 * Deliberate, and restored after a review pass read it as the UI overriding a
 * choice. Nothing behind them changes, so there is nothing to hold off. */
const RE_ARM_MS = 2600;

export default function StrategicDirections(props) {
    /* The switches flip themselves on when the section arrives, staggered, and
     * any that is off re-arms after RE_ARM_MS. */
    const [on, setOn] = useState(() => ITEMS.map(() => false));
    const [arrived, setArrived] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return undefined;

        /* An IntersectionObserver, not a scroll listener that called
         * getBoundingClientRect() on every scroll event and never
         * unsubscribed once it had fired. */
        let timers = [];
        const observer = new IntersectionObserver(
            (entries, obs) => {
                if (!entries.some((e) => e.isIntersecting)) return;
                obs.disconnect();
                setArrived(true);
                const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                ITEMS.forEach((item, i) => {
                    const flip = () =>
                        setOn((prev) => {
                            const next = [...prev];
                            next[i] = true;
                            return next;
                        });
                    if (reduce) flip();
                    else timers.push(setTimeout(flip, STAGGER_MS[i]));
                });
            },
            { threshold: 0.2 }
        );
        observer.observe(el);
        return () => {
            observer.disconnect();
            timers.forEach(clearTimeout);
        };
    }, []);

    /* One timer per switch that is off, cleared and re-armed by the same
     * effect — not the old version's single 850ms timer that re-fired for
     * every switch and could not be interrupted. */
    useEffect(() => {
        if (!arrived) return undefined;
        const timers = on
            .map((isOn, i) =>
                isOn
                    ? null
                    : setTimeout(
                          () =>
                              setOn((prev) => {
                                  const next = [...prev];
                                  next[i] = true;
                                  return next;
                              }),
                          RE_ARM_MS
                      )
            )
            .filter(Boolean);
        return () => timers.forEach(clearTimeout);
    }, [arrived, on]);

    const toggle = (i) => {
        setOn((prev) => {
            const next = [...prev];
            next[i] = !next[i];
            return next;
        });
    };

    return (
        <section
            ref={(node) => {
                sectionRef.current = node;
                if (typeof props.sectionRef === 'function') props.sectionRef(node);
                else if (props.sectionRef) props.sectionRef.current = node;
            }}
            id="approach"
            className="flex min-h-[100svh] bg-colorBackWhite w-full StrategicDirections">
            <div className="flex-1 flex flex-col mt-[52px] c-md:mt-[56px] c-lg:mt-[90px] c-xl:mt-[125px]">
                <div data-reveal className="text-black text-center px-[48px]">
                    <h2 className="text-36 c-xl:text-60 text-colorSemiBlack font-bold">HAVE YOU THOUGHT ABOUT THESE?</h2>
                    <p className="text-22 c-lg:text-30 c-xl:text-36 text-colorSemiBlack mt-[32px] c-md:mt-[40px] c-lg:mt-[48px] w-full font-medium">
                        If not, don’t worry.<br className="md:hidden" /> We’ve got you covered!
                    </p>
                </div>

                <div className="h-full w-full flex flex-col items-center justify-start">
                    <p data-reveal style={{ '--reveal-delay': '120ms' }} className="text-14 c-md:text-18 c-xl:text-28 mt-[8px] text-colorSemiBlack">
                        A peaceful night's sleep is unparalleled.
                    </p>
                    <div className="h-full w-full flex text-center items-center justify-center mt-[-36px]">
                        <div className="w-full c-lg:flex items-start justify-start mt-[62px] mb-[50px]">
                            {ITEMS.map((item, i) => (
                                <div key={item.key} className="w-full c-lg:w-1/3">
                                    <SwitchItem item={item} isOn={on[i]} onToggle={() => toggle(i)} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function SwitchItem({ item, isOn, onToggle }) {
    const { key, title, subtitle, description } = item;
    return (
        <div className="flex flex-col text-left items-center justify-center align-middle mx-[34px] nav-sm:mx-[68px] pt-[25px] pb-[29px] c-lg:pt-[48px] c-lg:pb-[57px]">
            {/* Two layouts, one control each. `flex ... hidden c-lg:flex` on a
                single element is three conflicting display utilities whose
                winner is decided by stylesheet order, not by what is written. */}
            <div className="flex justify-between w-full c-lg:hidden">
                <p className="text-24 font-semibold">{title}</p>
                <input
                    checked={isOn}
                    onChange={onToggle}
                    type="checkbox"
                    id={`switch-${key}-sm`}
                    aria-label={`${title}: ${subtitle}`}
                    className="switch-input switch-input-sm"
                />
                <label htmlFor={`switch-${key}-sm`} className="switch-label switch-label-sm mt-2" />
            </div>

            <div className="hidden c-lg:flex flex-col items-center">
                <input
                    checked={isOn}
                    onChange={onToggle}
                    type="checkbox"
                    id={`switch-${key}`}
                    aria-label={`${title}: ${subtitle}`}
                    className="switch-input"
                />
                <label htmlFor={`switch-${key}`} className="switch-label" />
                <p className="text-48 font-bold mt-[62px] text-colorSemiBlack">{title}</p>
            </div>

            <p className="text-14 c-md:text-16 c-xl:text-20 w-full mt-[12px] c-lg:mt-[62px] c-lg:text-center text-colorSemiBlack font-bold">{subtitle}</p>
            <p className="text-14 c-xl:text-18 w-full c-lg:w-[264px] c-xl:w-[330px] mt-2 c-lg:mt-[32px] px-0 text-colorTextSemiGray c-lg:text-center">{description}</p>
        </div>
    );
}
